import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Instagram, Search, MessageCircle, ArrowRight } from 'lucide-react';

interface OnboardingOverlayProps {
  onDismiss: () => void;
}

export function OnboardingOverlay({ onDismiss }: OnboardingOverlayProps) {
  const [currentStep, setCurrentStep] = useState(0);
  
  const steps = [
    {
      title: "인스타챗에 오신 것을 환영합니다",
      description: "인스타그램 계정과 대화하는 AI 서비스를 시작하세요.",
      icon: <Instagram className="h-12 w-12 text-primary" />,
      content: "인스타챗 AI는 인스타그램 계정을 분석하고 인공지능으로 데이터를 학습시켜 '그'사람과 대화하는 경험이 가능한 AI 챗봇입니다. 지금 인스타그램 계정을 연결해 보세요."
    },
    {
      title: "인스타그램 계정 검색하기",
      description: "원하는 계정의 사용자명을 입력하세요.",
      icon: <Search className="h-12 w-12 text-primary" />,
      content: "관심있는 인스타그램 계정의 사용자명만 입력하면 프로필을 불러올 수 있습니다. 최근 검색 목록에서 빠르게 재검색할 수도 있어요."
    },
    {
      title: "AI 프로필 사진 교환",
      description: "AI 프로필 생성이 가능한 시각형 AI 대화 시뮬레이터",
      icon: <MessageCircle className="h-12 w-12 text-primary" />,
      content: "단순히 채팅이 아닙니다. AI 이미지 생성 기술로 상대방의 인스타그램 피드 사진 데이터를 기반으로 학습한 AI가 대화중 상대방 사진을 생성하여 채팅을 더욱 사실적으로 승화합니다."
    }
  ];

  // 다음 단계로 이동
  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onDismiss();
      localStorage.setItem('onboarding-completed', 'true');
    }
  };
  
  // 스킵
  const handleSkip = () => {
    onDismiss();
    localStorage.setItem('onboarding-completed', 'true');
  };

  useEffect(() => {
    // 배경 스크롤 방지
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const step = steps[currentStep];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-md animate-fade-in mx-4">
        <div className="absolute right-0 top-0 p-4">
          <Button variant="ghost" size="sm" onClick={handleSkip} className="h-auto text-xs text-muted-foreground">
            모두 건너뛰기
          </Button>
        </div>
        
        <CardHeader className="text-center pb-2">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 mb-4">
            {step.icon}
          </div>
          <CardTitle className="text-xl">{step.title}</CardTitle>
          <CardDescription>{step.description}</CardDescription>
        </CardHeader>
        
        <CardContent className="text-center py-6">
          <p className="text-muted-foreground">{step.content}</p>
          
          <div className="flex justify-center gap-1.5 mt-6">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-1.5 rounded-full transition-all ${
                  index === currentStep
                    ? 'w-8 bg-primary'
                    : 'w-2.5 bg-muted'
                }`}
              />
            ))}
          </div>
        </CardContent>
        
        <CardFooter>
          <Button 
            className="w-full gradient-button"
            onClick={handleNextStep}
          >
            {currentStep < steps.length - 1 ? (
              <>다음 <ArrowRight className="ml-2 h-4 w-4" /></>
            ) : (
              '시작하기'
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}